import { MandateModel } from "../Models/MandateModel";
import { ActionResponse, ActionStatusHelper, RecordMap, useAuditEnricher } from "@/Shared"
import { useContact, useProperty, useNeed } from "@/OneLiners";
import { useDocumentEnricher } from "@/Domains/Document";


/**
 * useMandateEnricher hook – aggregates and enriches Mandate domain data.
 *
 * Author: Samano CASTRE
 * Date: January 29, 2026
 *
 * Responsibilities:
 * - Fetches Mandate-related dependencies (contacts, properties, needs)
 * - Delegates audit enrichment to the centralized useAuditEnricher hook
 * - Enriches Mandate entities with agent and linked domain relations
 * - Combines multiple async states into a single unified response object
 * - Exposes utility methods for retrieving enriched mandates (single or list)
 *
 * Architecture:
 * - Audit enrichment is fully delegated to useAuditEnricher (O(1) user lookups via Map)
 * - Domain enrichment (contacts, properties, needs) is handled locally
 * - Keeps audit logic fully centralized and DRY across all domains
 *
 * Behavior:
 * - Resolves agent user from agentId
 * - Enriches audit fields (createdBy, updatedBy, deletedBy, restoredBy)
 * - Links related contacts, properties, and needs through their respective lists
 * - Safely handles missing dependencies (null or empty arrays)
 * - Provides a unified API for higher-level hooks or UI components
 */
export function useMandateEnricher() {
    
    /**
     * Contacts data – used to enrich mandate contact links
     */
    const { 
        stateResponse: contactResponse, 
        dataMap: contactMap 
    } = useContact();
    
    /**
     * Properties data – used to enrich mandate property links
     */
    const { 
        stateResponse: propertyResponse, 
        dataMap: propertyMap 
    } = useProperty();
    
    /**
     * Needs data – used to enrich mandate need links
     */
    const { 
        stateResponse: needResponse, 
        dataMap: needMap 
    } = useNeed();

    /**
     * Centralized audit enrichment hook
     * - Provides O(1) audit user resolution via precomputed Map
     * - Exposes audit status and error
     */
    const {
        enricherResponse : auditEnricherResponse,
        userMap,
        getUserById,
        getEnrichedAudit,
    } = useAuditEnricher();

    /**
         * Document domain operations
         * Used to enrich mandates with associated documents
         */
        const {
            enricherResponse: documentEnricherResponse,
            findByResource
        } = useDocumentEnricher();

     /**
     * Combined response – aggregates status and errors from all dependencies
     */
    const enricherResponse: ActionResponse = {
        status: ActionStatusHelper.combine(
            contactResponse.status,
            auditEnricherResponse.status,
            propertyResponse.status,
            needResponse.status,
            documentEnricherResponse.status
        ),

        error: contactResponse.error ??
            auditEnricherResponse.error ??
            propertyResponse.error ??
            needResponse.error ??
            documentEnricherResponse.error
    };

     /** Enrich an entire list of mandates */
        const getEnrichedMandateMap = (mandateMap: RecordMap<MandateModel>): RecordMap<MandateModel> => {
    
            return mandateMap.map<MandateModel>(getStrictEnrichedMandate);
        }


    const getEnrichedMandate = (mandate?: MandateModel) : MandateModel | undefined => {
        if(!mandate) return undefined;
        return getStrictEnrichedMandate(mandate);
    }

    /**
     * Enriches a single Mandate entity
     * - Adds audit data (createdBy, updatedBy, etc.)
     * - Resolves agent from user list
     * - Attaches related contacts, properties, and needs
     */
    const getStrictEnrichedMandate = (mandate: MandateModel)  => {
        
        return {
            ...getEnrichedAudit(mandate),
            agent: getUserById(mandate.agentId),
            need : mandate.needId ? needMap.get(mandate.needId) : null,
            property : mandate.propertyId? propertyMap.get(mandate.propertyId) : null,
            mandateContactLinks: (mandate.mandateContactLinks ?? []).map(mcLink => {
                return { 
                    ...mcLink, 
                    contact: contactMap.get(mcLink.contactId) 
                };
            })
        } as MandateModel
    }

    /**
     * Returns an enriched Mandate by ID
     * - Returns undefined if the Mandate does not exist
     */
    const getEnrichedMandateById = (mandateMap: Map<number, MandateModel>,
        mandateId: number): MandateModel | undefined => {
        return getEnrichedMandate(mandateMap.get(mandateId));
    }

    /**
     * Public API – exposed to consumers of the hook
     */
    return {
        contactMap,
        userMap,
        propertyMap,
        needMap,
        enricherResponse,
        findByDocumentResource : findByResource,
        getEnrichedMandate,
        getStrictEnrichedMandate,
        getEnrichedMandateById,
        getEnrichedMandateMap,
    }
}